// The support overview's flat list of every payment under a user's email: the first consumer of the
// full-page table component (client/components/me_table). Client-backed: one request loads the complete set.

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'

import { MeTable } from '@CommonComponents'
import { client } from '@Utils'
import {
  formatDate,
  formatNumber,
  isNumber,
  searchTexts,
} from '../../components/me_table/format'

const money = value => formatNumber(value, 'currency')

// a value we don't have (no Stripe charge, no amount recorded) shows a dash, not a blank
const Missing = () => <span className="text-muted">—</span>

const StatusCell = ({ row }) => (
  <>
    <span
      className={`me-badge-tint me-badge-tint-${row.status.family}`}
      title={row.status.detail || undefined}
    >
      {row.status.icon && (
        <i
          className={`fa-regular fa-${row.status.icon} me-badge-ico`}
          aria-hidden="true"
        />
      )}
      {row.status.label}
    </span>
    {row.uncertain && (
      <span
        className="text-warning font-weight-bold ml-1"
        title="Couldn't reach Stripe, so refunds weren't checked. This status is from our records only."
      >
        ?
      </span>
    )}
  </>
)

StatusCell.propTypes = { row: PropTypes.object.isRequired }

export const tableConfig = email => ({
  tableId: 'support-overview-payments',
  title: 'All payments',
  headingLevel: 3,
  caption: 'All payments',
  description: `Every payment made under ${email}, including ones not tied to a current course.`,
  rowKey: 'id',
  columns: [
    {
      key: 'date',
      label: 'Date',
      type: 'date',
      sortable: true,
      searchable: true,
      required: true,
      wrap: 'nowrap',
    },
    {
      key: 'amount',
      label: 'Charged',
      type: 'numeric',
      format: 'currency',
      sortable: true,
      searchable: true,
      summary: 'sum',
      value: row => row.amount,
      searchText: row =>
        searchTexts(
          { key: 'amount', type: 'numeric', format: 'currency' },
          row
        ).join(' '),
      render: row => (isNumber(row.amount) ? money(row.amount) : <Missing />),
    },
    {
      key: 'status',
      label: 'Status',
      type: 'badge',
      sortable: true,
      searchable: true,
      filterable: true,
      value: row => row.status.label,
      searchText: row => row.status.label,
      render: row => <StatusCell row={row} />,
    },
    {
      key: 'charge',
      label: 'Stripe charge',
      type: 'text',
      sortable: true,
      searchable: true,
      value: row => row.charge,
      searchText: row => row.charge,
      render: row => row.charge || <Missing />,
      wrap: 'nowrap',
      className: 'me-table-id',
    },
    {
      key: 'courses',
      label: 'Courses',
      type: 'text',
      sortable: true,
      searchable: true,
    },
  ],
  defaultSort: { key: 'date', direction: 'descending' },
  pageSizes: [10, 25, 50],
  defaultPageSize: 10,
  source: { mode: 'client', max: 2000 },
  searchLabel: 'Search payments',
  searchPlaceholder: 'Search Stripe charge, course, or amount',
  emptyState: {
    title: 'No payments',
    description: 'No payments have been made under this email.',
  },
  summary: { label: 'Total charged' },
})

const openDialog = options =>
  window.$ && window.$.meDialog && window.$.meDialog.open2(options)

export const rowActions = row => {
  const actions = [
    {
      key: 'status',
      label: 'Change status',
      icon: 'fa-pen-to-square',
      onSelect: () => openDialog(row.actions.status_dialog),
    },
  ]
  if (row.actions.details_url) {
    actions.push({
      key: 'details',
      label: 'View details',
      icon: 'fa-magnifying-glass-dollar',
      onSelect: () => window.location.assign(row.actions.details_url),
    })
  }
  actions.push({
    key: 'receipt',
    label: 'Receipt',
    icon: 'fa-receipt',
    onSelect: () => window.location.assign(row.actions.receipt_url),
  })
  return actions
}

export const PaymentDetail = ({ row }) => (
  <div>
    <h4 className="me-ssh">Items</h4>
    <table className="table table-condensed w-auto mb-2">
      <tbody>
        {row.items.map((item, i) => (
          <tr key={i} className={item.valid ? undefined : 'text-muted'}>
            <td className="pr-5">{item.label}</td>
            <td className="pr-5 text-right">{money(item.price)}</td>
            <td className="pr-5">{item.valid ? '' : 'invalid'}</td>
            <td>{item.end_date ? `until ${formatDate(item.end_date)}` : ''}</td>
          </tr>
        ))}
      </tbody>
    </table>
    <p className="small text-muted mb-1">
      Paid {formatDate(row.date)} at {row.time}
    </p>
    {row.refunded > 0 && (
      <p className="small mb-1">Stripe refunded {money(row.refunded)}.</p>
    )}
    {row.notes && (
      <p className="small font-italic mb-1">
        <i
          className="fa-solid fa-comment text-danger mr-1"
          aria-hidden="true"
        />
        {row.notes}
      </p>
    )}
    {row.temp_access.map((grant, i) => (
      <p key={i} className="small mb-1">
        Temporary access granted {formatDate(grant.granted)}
        {grant.by ? ` by ${grant.by}` : ''}, until {formatDate(grant.until)}.
      </p>
    ))}
  </div>
)

PaymentDetail.propTypes = { row: PropTypes.object.isRequired }

export const PaymentsTable = ({ dataUrl, email }) => {
  const [status, setStatus] = useState('loading')
  const [rows, setRows] = useState([])
  const config = useMemo(() => tableConfig(email), [email])

  const load = useCallback(() => {
    setStatus('loading')
    client
      .get(dataUrl)
      .then(response => {
        setRows(response.data.rows)
        setStatus('ready')
      })
      .catch(() => setStatus('error'))
  }, [dataUrl])

  useEffect(() => load(), [load])

  return (
    <MeTable
      config={config}
      rows={rows}
      status={status}
      onRetry={load}
      rowActions={rowActions}
      renderDetail={row => <PaymentDetail row={row} />}
    />
  )
}

PaymentsTable.propTypes = {
  dataUrl: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
}
